import FroalaEditor from 'froala-editor';
import i18next from 'i18next';
import { debounce } from 'lodash';

import { INSERT_MENTION_ICON_PATH } from '../../use-froala-icons';

import { MentionSelectors } from './constants';

const PLUGIN_NAME = 'mention';

(function (FroalaEditor) {
  // Add an option for your plugin.
  FroalaEditor.DEFAULTS = Object.assign(FroalaEditor.DEFAULTS, {
    enableMentions: false,
  });

  FroalaEditor.DefineIcon('insertMention', {
    NAME: 'insertMention',
    PATH: INSERT_MENTION_ICON_PATH,
  });

  FroalaEditor.RegisterCommand('insertMention', {
    title: i18next.t('COMMON.INSERT_MENTION'),
    icon: 'insertMention',
    focus: false,
    undo: false,
    refreshAfterCallback: false,
    callback: function () {
      this.selection.save();
      this.events.trigger('mention.buttons.insert', []);
    },
  });

  // The editor parameter is the current instance.
  FroalaEditor.PLUGINS[PLUGIN_NAME] = function (editor) {
    const isEnabled = editor.opts.enableMentions === true;

    let isActive = false;
    let editState: {
      node: Node;
      startOffset: number;
      endOffset: number;
    } | null = null;

    // Detect Safari (exclude Chrome/Chromium on iOS which also include 'Safari').
    function isSafariBrowser(): boolean {
      const ua = navigator.userAgent;
      return (
        /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium\//.test(ua)
      );
    }

    // Safari-safe insertion when target is a Text node: split text and insert element
    function safariInsertIntoTextNode(
      textNode: Text,
      startOffset: number,
      endOffset: number,
      element: Element
    ): boolean {
      const parent = textNode.parentNode;
      if (!parent) return false;

      const data = textNode.data ?? '';
      const before = data.slice(0, startOffset);
      const after = data.slice(endOffset);

      const frag = document.createDocumentFragment();
      if (before.length > 0) frag.appendChild(document.createTextNode(before));
      frag.appendChild(element);
      if (after.length > 0) frag.appendChild(document.createTextNode(after));

      try {
        parent.replaceChild(frag, textNode);
        return true;
      } catch (e) {
        return false;
      }
    }

    function _init() {
      if (!isEnabled) return;

      function detectMention() {
        const range: Range = editor.selection.ranges(0);

        if (!range.collapsed) {
          return;
        }

        const { startContainer: container, endOffset } = range;

        if (container.nodeType !== document.TEXT_NODE) {
          return;
        }

        // If a selection is inside a mention already (for some reason), ignore
        // this event.
        if (container.parentElement.closest(MentionSelectors.MentionUser)) {
          return;
        }

        const text = container.nodeValue;

        const startOffset = text.lastIndexOf('@', endOffset);

        if (startOffset === -1) {
          return;
        }

        const isAtNodeStart = () => startOffset === 0;
        const isAfterWhiteSpace = () =>
          startOffset > 0 && text.charAt(startOffset - 1).match(/\s/);
        const isEmpty = () => endOffset === startOffset + 1;
        const isFollowedByWord = () =>
          text.length > startOffset + 1 &&
          text.charAt(startOffset + 1).match(/\w/);

        const isValidMentionAnchor =
          (isAtNodeStart() || isAfterWhiteSpace()) &&
          (isEmpty() || isFollowedByWord());

        if (!isValidMentionAnchor) {
          return;
        }

        const mentionRange = new Range();
        mentionRange.setStart(container, startOffset);
        mentionRange.setEnd(container, endOffset);

        const clientRects = mentionRange.getClientRects();

        if (clientRects.length === 0) {
          return;
        }

        const clientRect = clientRects[0];

        return {
          text: text.slice(startOffset + 1, endOffset),
          node: container,
          startOffset,
          endOffset,
          position: clientRect,
        };
      }

      // Use debounce() cause reacting to every input or keydown events generates
      // a lot of updates and causes an error: ‘Maximum update depth exceeded’.
      const handleInput = debounce(() => {
        const detected = detectMention();

        if (detected) {
          editState = detected;
          editor.events.trigger('mention.input', [
            { text: detected.text, position: detected.position },
          ]);
        } else {
          editState = null;
          editor.events.trigger('mention.reset');
        }
      }, 100);

      editor.events.on('input', () => {
        handleInput();
      });

      // The "input" event is usually enough, but it's not called for some of
      // the actions. For some reason, it's not called when you delete or cut or
      // paste text. The "contentChanged" event is called every time after
      // something is changed, but there is a delay before the state settles. So
      // in many cases the popover will already be displayed when this is called
      // and this will not do anything, but this is used as a fallback for cases
      // that are not handled by other events.
      editor.events.on('contentChanged', () => {
        handleInput();
      });

      editor.events.on('keydown', (event: KeyboardEvent) => {
        switch (event.key) {
          case 'Backspace':
          case 'Delete': {
            handleInput();
            break;
          }
        }
      });

      editor.events.on(
        'keydown',
        (event: KeyboardEvent) => {
          if (!isActive) {
            // Mention popover is not active, ignoring key.
            return;
          }

          switch (event.key) {
            case 'ArrowUp':
            case 'ArrowDown':
            case 'Tab':
            case 'Enter': {
              event.preventDefault();
              editor.events.trigger('mention.keydown', [event.key]);
              // Prevent the enter from adding a new line.
              if (event.key === 'Enter' && isActive) {
                return false;
              }
              break;
            }

            case 'Escape':
            case 'ArrowLeft':
            case 'ArrowRight':
            case 'PageUp':
            case 'PageDown':
            case 'Home':
            case 'End': {
              event.stopPropagation();
              editor.selection.save();
              editor.events.trigger('mention.reset', []);
              break;
            }
          }
        },
        true
      );
    }

    return {
      _init,

      // Shared internal helper to perform insertion either using current selection
      // or the stored editState range. Handles Safari-specific path and fallbacks.
      _performInsert(element: Element, opts: { useEditState: boolean }) {
        if (!isEnabled) return;

        const { useEditState } = opts;

        // Ensure focus and keep current selection
        editor.events.focus();
        editor.selection.save();

        // Determine target range/positions
        let startNode: Node;
        let startOffset: number;
        let endOffset: number;
        let range: Range | null = null;

        if (useEditState) {
          if (!editState) {
            // eslint-disable-next-line no-console
            console.error(
              '[mention] _performInsert called with no active edit state'
            );
            return;
          }
          startNode = editState.node;
          startOffset = editState.startOffset;
          endOffset = editState.endOffset;
        } else {
          editor.selection.restore();
          range = editor.selection.ranges(0);

          startNode = range.startContainer;
          startOffset = range.startOffset ?? 0;
          endOffset = range.collapsed
            ? startOffset
            : range.endOffset ?? startOffset;
        }
        try {
          // Safari-safe path when inserting into a Text node
          if (isSafariBrowser() && startNode?.nodeType === Node.TEXT_NODE) {
            const didInsert = safariInsertIntoTextNode(
              startNode as Text,
              startOffset,
              endOffset,
              element
            );
            if (didInsert) {
              setTimeout(() => {
                editor.events.focus();
                editor.selection.setAfter(element);
                editor.selection.restore();
                editor.html.insert(' ');
              }, 0);
              if (useEditState) editState = null;
              return;
            }
          }

          // Non-Safari or fallback path via Range
          if (!range) {
            range = new Range();
            range.setStart(startNode, startOffset);
            range.setEnd(startNode, endOffset);
          }

          range.deleteContents();
          range.insertNode(element);

          setTimeout(() => {
            editor.events.focus();
            editor.selection.setAfter(element);
            editor.selection.restore();
            editor.html.insert(' ');
          }, 0);

          if (useEditState) editState = null;
        } catch (e) {
          // Original code from insert
          console.error('[mention] _performInsert error', e);
          try {
            editor.html.insert((element as HTMLElement).outerHTML + ' ');
            setTimeout(() => {
              editor.selection.restore();
            }, 0);
          } catch (fallbackError) {
            console.error(
              '[mention] _performInsert fallback failed',
              fallbackError
            );
          }
          if (useEditState) editState = null;
          return;
        }
      },

      setActive(active: boolean) {
        if (!isEnabled) return;

        isActive = active;
      },

      replace(element: Element) {
        if (!isEnabled) return;
        this._performInsert(element, {
          useEditState: true,
        });
      },

      async insert(element: Element) {
        if (!isEnabled) return;

        const doInsert = () => {
          if (isSafariBrowser()) {
            this._performInsert(element, {
              useEditState: false,
            });
            return;
          }

          editor.selection.restore();
          const range: Range = editor.selection.ranges(0);

          range.deleteContents();
          range.insertNode(element);

          editor.events.focus();
          editor.selection.setAfter(element);

          editor.selection.restore();
          editor.html.insert(' ');
        };

        await new Promise<void>((resolve) =>
          setTimeout(() => {
            doInsert();
            resolve();
          }, 0)
        );
      },
    };
  };
})(FroalaEditor);
